/**
 * The queue (§08).
 *
 * Ordered by SLA deadline, never by arrival — a suspected-minor report filed at
 * 4pm sits above a spam report filed at noon because it is due first. The API
 * returns it that way and this screen does not re-sort it; the only thing the
 * console adds is a clock that runs, because "due in 12 minutes" and "due in 12
 * hours" are the same sentence until you can see them tick.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getQueue } from '../api';
import type { QueueCase, QueueResponse } from '../api';
import {
  AccountChip,
  AdvisoryBlock,
  Countdown,
  EmptyState,
  ErrorState,
  SEVERITIES,
  SEVERITY_LABEL,
  SeverityStripe,
  Skeletons,
  clusterName,
  formatTimestamp,
  severityFor,
  shortId,
  useNow,
} from './kit';

type StatusFilter = 'open' | 'closed' | 'all';

const LIMITS = [25, 50, 100];
const ONE_HOUR_MS = 60 * 60 * 1000;

export default function Queue({
  onOpenCase,
  onOverdueCount,
}: {
  onOpenCase: (caseId: string) => void;
  onOverdueCount: (n: number | null) => void;
}) {
  const [status, setStatus] = useState<StatusFilter>('open');
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState<QueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const now = useNow(1000);

  // Kept in a ref so the badge callback never becomes a fetch dependency.
  const reportCount = useRef(onOverdueCount);
  reportCount.current = onOverdueCount;

  const load = useCallback(async (nextStatus: StatusFilter, nextLimit: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getQueue({ status: nextStatus, limit: nextLimit });
      setData(res);
      if (nextStatus === 'open') reportCount.current(res.counts.overdue);
    } catch (err) {
      setData(null);
      setError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
      reportCount.current(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status, limit);
  }, [load, status, limit]);

  const cases = data?.cases ?? [];
  const openCases = cases.filter((c) => c.status === 'open');
  const overdue = cases.filter((c) => c.overdue);
  const dueWithinHour = openCases.filter((c) => {
    const remaining = Date.parse(c.dueAt) - now;
    return remaining > 0 && remaining < ONE_HOUR_MS;
  });

  const bySeverity = SEVERITIES.map((sev) => ({
    severity: sev,
    count: openCases.filter((c) => severityFor(c.slaMins) === sev).length,
  }));
  const severityTotal = bySeverity.reduce((sum, s) => sum + s.count, 0);

  return (
    <>
      <div className="page-intro">
        <h2 className="eyebrow">Queue</h2>
        <p>
          Ordered by SLA deadline, not arrival time (§08). The clock on the right is the first-response
          target from the report taxonomy — it turns red the moment it is spent.
        </p>
      </div>

      <div className="summary">
        <div className="summary-cell">
          <span className="figure num">{loading && !data ? '—' : openCases.length}</span>
          <span className="sub">open on this page</span>
        </div>
        <div className={`summary-cell ${overdue.length > 0 ? 'is-alert' : 'is-calm'}`}>
          <span className="figure num">{loading && !data ? '—' : overdue.length}</span>
          <span className="sub">{overdue.length === 1 ? 'past its SLA' : 'past their SLA'}</span>
        </div>
        <div className="summary-cell">
          <span className="figure num">{loading && !data ? '—' : dueWithinHour.length}</span>
          <span className="sub">due within the hour</span>
        </div>
        <div className="summary-cell" style={{ gridColumn: 'span 2' }}>
          <span className="sub">severity mix, open cases</span>
          <div className="sevbar" role="img" aria-label="Severity distribution of open cases">
            {severityTotal === 0 ? null : (
              bySeverity.map((s) =>
                s.count === 0 ? null : (
                  <span
                    key={s.severity}
                    className={`sev-${s.severity}`}
                    style={{ width: `${(s.count / severityTotal) * 100}%` }}
                  />
                ),
              )
            )}
          </div>
          <div className="sev-legend">
            {bySeverity.map((s) => (
              <span key={s.severity}>
                <i style={{ background: swatch(s.severity) }} />
                {SEVERITY_LABEL[s.severity]} <b className="num">{s.count}</b>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Case status filter">
          {(['open', 'closed', 'all'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <label className="chip" htmlFor="queue-limit" style={{ gap: 8 }}>
          show
          <select
            id="queue-limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ background: 'transparent', border: 0, fontWeight: 700 }}
          >
            {LIMITS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>

        <div style={{ flex: 1 }} />

        <button type="button" className="btn btn-sm" onClick={() => void load(status, limit)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading && !data && <Skeletons count={4} height={132} />}

      {error && (
        <ErrorState
          title="The queue did not load"
          message={error.message}
          code={`${error.code}${error.status ? ` · HTTP ${error.status}` : ''}`}
          onRetry={() => void load(status, limit)}
        />
      )}

      {!loading && !error && cases.length === 0 && (
        <EmptyState
          title={status === 'open' ? 'Nothing is waiting on a moderator' : 'No cases match that filter'}
          action={
            status !== 'all' ? (
              <button type="button" className="btn btn-sm" onClick={() => setStatus('all')}>
                Show every case
              </button>
            ) : undefined
          }
        >
          {status === 'open'
            ? 'The open queue is empty. Reports raised from the app land here automatically, with a deadline attached from the §08 taxonomy.'
            : 'Nothing has been recorded under this filter yet.'}
        </EmptyState>
      )}

      {cases.length > 0 && (
        <div className="case-list">
          {cases.map((c) => (
            <CaseRow key={c.caseId} item={c} now={now} onOpen={() => onOpenCase(c.caseId)} />
          ))}
        </div>
      )}

      {data && cases.length > 0 && (
        <p className="eyebrow" style={{ marginTop: 16 }}>
          {data.orderedBy}
        </p>
      )}
    </>
  );
}

function swatch(severity: string): string {
  if (severity === 'critical') return 'var(--alta)';
  if (severity === 'high') return 'var(--brass)';
  if (severity === 'elevated') return 'var(--indigo)';
  return 'var(--river)';
}

function CaseRow({ item, now, onOpen }: { item: QueueCase; now: number; onOpen: () => void }) {
  const severity = severityFor(item.slaMins);
  const live = item.status === 'open';

  return (
    <article className={`case-row ${item.overdue ? 'is-overdue' : ''}`}>
      <SeverityStripe severity={severity} />

      <div className="case-main">
        <div className="case-title">
          <h3>{item.reasonLabel}</h3>
          <span className="chip chip-mono">{item.reason}</span>
          <span className="chip">{SEVERITY_LABEL[severity]}</span>
          {item.evidenceHold && <span className="chip chip-alta">evidence hold</span>}
          {item.subject.profileHidden && <span className="chip chip-brass">profile hidden</span>}
          {item.status !== 'open' && (
            <span className="chip chip-indigo">
              {item.status}
              {item.verdict ? ` · ${item.verdict.replace(/_/g, ' ')}` : ''}
            </span>
          )}
        </div>

        <div className="case-subject">
          <b>{item.subject.displayName}</b> · {clusterName(item.subject.clusterId)} ·{' '}
          <span className="num">{shortId(item.subject.userId)}</span> ·{' '}
          {item.reportCount === 1 ? '1 report' : `${item.reportCount} reports`} on this account ·
          opened {formatTimestamp(item.openedAt)} · via {item.source.replace(/_/g, ' ')}
        </div>

        <div className="chip-row">
          <AccountChip status={item.subject.accountStatus} />
        </div>

        <AdvisoryBlock
          summary={item.triage.summary}
          suggestedVerdict={item.triage.suggestedVerdict}
          confidence={item.triage.confidence}
          source={item.triage.source}
        />
      </div>

      <div className="case-side">
        {item.overdue && <span className="overdue-flag">overdue</span>}
        <Countdown dueAt={item.dueAt} now={now} slaLabel={item.slaLabel} live={live} />
        <button type="button" className="btn btn-sm btn-primary" onClick={onOpen}>
          Open case
        </button>
      </div>
    </article>
  );
}
